import { createContext, useContext, useState, useCallback } from 'react';
import { buildCustomThemeVars } from '../utils/color';

const THEME_KEY        = 'tmg_theme';
const CUSTOM_THEME_KEY = 'tmg_active_custom_theme';
const STYLE_TAG_ID     = 'tmg-custom-theme';

export const THEMES = ['standard', 'light', 'black', 'high-contrast'];

const ThemeContext = createContext({
  theme: 'standard',
  setTheme: () => {},
  activeCustomThemeId: null,
  setActiveCustomThemeId: () => {},
  customThemes: [],
  setCustomThemes: () => {},
});

export function useTheme() {
  return useContext(ThemeContext);
}

function applyBuiltInTheme(theme) {
  const root = document.documentElement;
  THEMES.forEach((t) => {
    if (t !== 'standard') root.classList.remove(`theme-${t}`);
  });
  if (theme !== 'standard') {
    root.classList.add(`theme-${theme}`);
  }
}

function injectCustomThemeStyle(vars) {
  let tag = document.getElementById(STYLE_TAG_ID);
  if (!tag) {
    tag = document.createElement('style');
    tag.id = STYLE_TAG_ID;
    document.head.appendChild(tag);
  }
  const allVars = buildCustomThemeVars(vars);
  const declarations = Object.entries(allVars)
    .map(([k, v]) => `  ${k}: ${v};`)
    .join('\n');
  tag.textContent = `:root {\n${declarations}\n}`;
  try { localStorage.setItem('tmg_custom_bg', allVars['--c-bg-page'] || vars['--c-bg-page'] || ''); } catch { /* ignore */ }
}

function removeCustomThemeStyle() {
  document.getElementById(STYLE_TAG_ID)?.remove();
  try { localStorage.removeItem('tmg_custom_bg'); } catch { /* ignore */ }
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    try {
      const stored = localStorage.getItem(THEME_KEY);
      if (stored && THEMES.includes(stored)) {
        applyBuiltInTheme(stored);
        return stored;
      }
    } catch {
      // localStorage unavailable
    }
    const preferLight =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-color-scheme: light)').matches;
    const initial = preferLight ? 'light' : 'standard';
    applyBuiltInTheme(initial);
    return initial;
  });

  // Custom themes array (sourced from Firestore via Layout.jsx calling setCustomThemes)
  const [customThemes, setCustomThemesState] = useState([]);

  // Which custom theme is active (null = built-in theme is active)
  const [activeCustomThemeId, setActiveCustomThemeIdState] = useState(() => {
    try {
      return localStorage.getItem(CUSTOM_THEME_KEY) || null;
    } catch {
      return null;
    }
  });

  function setTheme(newTheme) {
    if (!THEMES.includes(newTheme)) return;
    // Switching to a built-in theme: remove custom theme style + deactivate custom ID
    removeCustomThemeStyle();
    setActiveCustomThemeIdState(null);
    try {
      localStorage.removeItem(CUSTOM_THEME_KEY);
    } catch { /* ignore */ }

    applyBuiltInTheme(newTheme);
    setThemeState(newTheme);
    try {
      localStorage.setItem(THEME_KEY, newTheme);
    } catch { /* ignore */ }
  }

  const applyCustomThemeById = useCallback((id, themes) => {
    const found = themes.find((t) => t.id === id);
    if (!found) return false;
    // Remove built-in theme classes so custom vars take full effect
    THEMES.forEach((t) => {
      if (t !== 'standard') document.documentElement.classList.remove(`theme-${t}`);
    });
    injectCustomThemeStyle(found.vars);
    return true;
  }, []);

  function setActiveCustomThemeId(id) {
    if (!id) {
      // Revert to current built-in theme
      removeCustomThemeStyle();
      setActiveCustomThemeIdState(null);
      try { localStorage.removeItem(CUSTOM_THEME_KEY); } catch { /* ignore */ }
      applyBuiltInTheme(theme);
      return;
    }
    const applied = applyCustomThemeById(id, customThemes);
    if (!applied) return;
    setActiveCustomThemeIdState(id);
    try {
      localStorage.setItem(CUSTOM_THEME_KEY, id);
    } catch { /* ignore */ }
  }

  /**
   * Called from Layout.jsx (inside AuthProvider) when profile.customThemes changes.
   * Replaces the custom theme list and re-applies the active custom theme if one is set.
   */
  const setCustomThemes = useCallback((themes) => {
    setCustomThemesState(themes);
    // Re-apply the active custom theme with the updated vars
    setActiveCustomThemeIdState((currentId) => {
      if (currentId) {
        const applied = applyCustomThemeById(currentId, themes);
        if (!applied) {
          // Theme was deleted — fall back to built-in
          removeCustomThemeStyle();
          try { localStorage.removeItem(CUSTOM_THEME_KEY); } catch { /* ignore */ }
          return null;
        }
      }
      return currentId;
    });
  }, [applyCustomThemeById]);

  return (
    <ThemeContext.Provider value={{
      theme,
      setTheme,
      activeCustomThemeId,
      setActiveCustomThemeId,
      customThemes,
      setCustomThemes,
    }}>
      {children}
    </ThemeContext.Provider>
  );
}
